import { useQuery } from 'react-query';
import React, { useState } from 'react';
import { QueryKey } from 'api/queryKey';
import Column from 'antd/lib/table/Column';
import { Checkbox } from 'antd';
import { Patient } from 'models/patient';
import { useEnsureContext } from 'common/hooks';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { Link } from 'react-router-dom';
import { BusinessPartnerRoutes } from 'pages/BusinessPartnerManagement/businessPartnerRoutes';
import { commonPaginationConfig, initialPageParams } from 'models/page';
import styled from 'styled-components';
import { LoadingOutlined } from '@ant-design/icons';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { NumberedTable } from 'common/components/NumberedTable';
import { useModuleContext } from 'common/ModuleContextProvider';

const CheckAll = styled.div`
  height: 45px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  .label {
    margin-bottom: 5px;
  }
`;

interface BusinessPartnerMedicalOperatorAssignedPatientsProps {
  medicalOperatorId: number;
  checkedPatientsIds: number[];
  setCheckedPatientsIds: React.Dispatch<React.SetStateAction<number[]>>;
}

export const BusinessPartnerMedicalOperatorAssignedPatients: React.FC<BusinessPartnerMedicalOperatorAssignedPatientsProps> = ({
  medicalOperatorId,
  checkedPatientsIds,
  setCheckedPatientsIds,
}) => {
  const bpContext = useEnsureContext(BusinessPartnerContext);
  const { rootUrl } = useModuleContext();
  const intlMessage = useFormatMessage();

  const [pageMetaData, setPageMetaData] = useState(initialPageParams);
  const [fetchAllPatientsEnabled, setFetchAllPatientsEnabled] = useState(false);

  const { isFetching, data } = useQuery(
    [
      QueryKey.BusinessPartnerMedicalOperatorPatientsList,
      medicalOperatorId,
      pageMetaData,
    ],
    () =>
      BusinessPartnersAPI.fetchMedicalOperatorPatients(
        bpContext.id,
        medicalOperatorId,
        {
          ...pageMetaData,
        }
      ),
    {
      keepPreviousData: true,
    }
  );

  const fetchAllPatients = useQuery(
    [QueryKey.BusinessPartnerMedicalOperatorPatientsList, medicalOperatorId],
    () =>
      BusinessPartnersAPI.fetchMedicalOperatorPatients(
        bpContext.id,
        medicalOperatorId,
        { ...pageMetaData, pageSize: data!.totalElements }
      ),
    {
      enabled: fetchAllPatientsEnabled,
      onSuccess: (data) => {
        setCheckedPatientsIds(data.content.map((b) => b.id));
        setFetchAllPatientsEnabled(false);
      },
    }
  );

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  return (
    <>
      <NumberedTable
        rowKey="id"
        loading={isFetching}
        dataSource={data?.content}
        pagination={paginationConfig}
        scroll={{ x: 'auto' }}
      >
        <Column
          title={() => (
            <>
              {data && data.totalElements > 0 && (
                <CheckAll>
                  {fetchAllPatients.isFetching ? (
                    <LoadingOutlined />
                  ) : (
                    <>
                      <Checkbox
                        indeterminate={
                          checkedPatientsIds.length > 0 &&
                          checkedPatientsIds.length !== data?.totalElements
                        }
                        checked={
                          checkedPatientsIds.length > 0 &&
                          checkedPatientsIds.length === data?.totalElements
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            fetchAllPatients.data
                              ? setCheckedPatientsIds(
                                  fetchAllPatients.data.content.map((b) => b.id)
                                )
                              : setFetchAllPatientsEnabled(true);
                          } else {
                            setCheckedPatientsIds([]);
                          }
                        }}
                      />
                      {`${checkedPatientsIds.length} / ${
                        data?.totalElements || '-'
                      }`}
                    </>
                  )}
                </CheckAll>
              )}
            </>
          )}
          key="actions"
          align="center"
          width={100}
          render={(value, record: Patient) => (
            <Checkbox
              key={record.id}
              onChange={(e) => {
                if (e.target.checked) {
                  setCheckedPatientsIds((ids: any[]) => [...ids, record.id]);
                } else {
                  setCheckedPatientsIds((ids: any[]) =>
                    ids.filter((id) => id !== record.id)
                  );
                }
              }}
              checked={checkedPatientsIds.includes(record.id)}
            />
          )}
        />
        <Column
          title={intlMessage({ id: 'name' })}
          dataIndex="firstName"
          render={(value, record: Patient) => (
            <Link
              key={record.id}
              to={`${rootUrl}/${BusinessPartnerRoutes.Patients}/${record.id}`}
            >
              {record.firstName + ' ' + record.lastName}
            </Link>
          )}
        />
      </NumberedTable>
    </>
  );
};
